import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ActionIcon, Alert, Anchor, Button, Checkbox, Select, Text, TextInput } from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconPlus, IconX } from '@tabler/icons-react'
import { ResourceInput } from '@medplum/react'
import { createReference } from '@medplum/core'
import type { Patient, Practitioner, Reference } from '@medplum/fhirtypes'

import { SNOMED_USER_ROLES } from '@/utils'
import type { FaxPatients } from '@/hooks/useFaxPatients'
import { useFaxSplit, type FaxSplitPlan } from '@/hooks/useFaxSplit'
import type { FaxDocumentType } from '@/hooks/useFaxTriage'
import { useFaxPatientCheck, useFaxPatientReview } from '@/hooks/useFaxPatientCheck'
import { formatPages } from './formatPages'
import { FaxPatientReviewConfirm, FaxPatientWarnings } from './FaxPatientWarnings'

type FaxPatientGroup = FaxPatients['groups'][number]

interface Row {
  // Stable across removals, so each row keeps its own (uncontrolled) chart picker.
  key: number
  pages: number[]
  patient: Reference<Patient> | null
  title: string
  // What the fax itself prints for this patient (OCR), shown so staff can check the chart.
  name: string | null
  dob: string | null
}

interface FaxSplitPanelProps {
  taskId: string
  faxPatients: FaxPatients
  onViewPage: (page: number) => void
  onFileAsOne: () => void
  defaultLabName?: string
}

// Files a fax holding several patients as one document per patient (PD-1988). Also shows an
// interrupted split (read-only, to finish) and a finished one (what was filed where).
export function FaxSplitPanel(props: FaxSplitPanelProps) {
  const { split } = props.faxPatients
  if (split?.state === 'completed') return <FiledDocuments filed={split.filed} />
  const claimedPlan = split?.state === 'in-progress' ? (split.plan ?? undefined) : undefined
  // Remount on claim so the form's state is rebuilt from the saved plan.
  return <SplitForm key={claimedPlan ? 'claimed' : 'open'} {...props} claimedPlan={claimedPlan} />
}

function SplitForm({
  taskId,
  faxPatients,
  onViewPage,
  onFileAsOne,
  defaultLabName,
  claimedPlan,
}: FaxSplitPanelProps & { claimedPlan?: FaxSplitPlan }) {
  const readOnly = !!claimedPlan
  const faxSplit = useFaxSplit(taskId)
  const { groups } = faxPatients

  const [documentType, setDocumentType] = useState<FaxDocumentType>(
    claimedPlan?.documentType ?? 'lab',
  )
  const [labName, setLabName] = useState(
    claimedPlan ? (claimedPlan.labName ?? '') : (defaultLabName ?? ''),
  )
  const [referringProvider, setReferringProvider] = useState<Reference<Practitioner> | null>(
    claimedPlan?.referringProviderId
      ? { reference: `Practitioner/${claimedPlan.referringProviderId}` }
      : null,
  )
  const [notifyPatient, setNotifyPatient] = useState(claimedPlan?.notifyPatient ?? true)
  const [rows, setRows] = useState<Row[]>(() =>
    claimedPlan ? rowsFromPlan(claimedPlan, groups) : withAtLeastTwoRows(rowsFromGroups(groups)),
  )
  const [pagesOpen, setPagesOpen] = useState(false)
  // Row keys stay unique after removals and additions.
  const nextKey = useRef(rows.length)
  // Pages OCR attached to a neighbour because they name no patient; flagged wherever they end up.
  const guessed = new Set(groups.flatMap((group) => group.suggestedPages))

  // Each row with a chart and pages is checked against what those pages name (PD-2054).
  const checked = rows.flatMap((row, index) => {
    const patientId = idOf(row.patient)
    return patientId && row.pages.length > 0 ? [{ index, pages: row.pages, patientId }] : []
  })
  const checkGroups = checked.map(({ pages, patientId }) => ({ pages, patientId }))
  const patientCheck = useFaxPatientCheck(
    taskId,
    checkGroups,
    !readOnly && faxPatients.pages.length > 0,
  )
  const patientReview = useFaxPatientReview(taskId)
  // Submit files the rows as they were when clicked: no edits while it is on its way.
  const locked = readOnly || patientReview.isPending || faxSplit.isPending
  const needsReview = !!patientCheck.data?.needsReview
  // A confirmation covers the warnings staff saw: changed rows need a new one (PD-2053).
  const [reviewConfirmed, setReviewConfirmed] = useState(false)
  useEffect(() => setReviewConfirmed(false), [patientCheck.data])
  const checkOf = (index: number) => {
    const position = checked.findIndex((c) => c.index === index)
    return position === -1 ? undefined : patientCheck.data?.groups[position]
  }

  // For a patient OCR missed: staff move that patient's pages into the new row.
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { key: nextKey.current++, pages: [], patient: null, title: '', name: null, dob: null },
    ])
    setPagesOpen(true)
  }

  // The OCR lab name can arrive after mount; fill it only into an empty Lab, as the single form does.
  useEffect(() => {
    if (!readOnly && defaultLabName) setLabName((current) => current || defaultLabName)
  }, [defaultLabName, readOnly])

  const isLab = documentType === 'lab'
  // Why Split is unavailable, shown under the button: a missing field can be scrolled out of view.
  const missing =
    rows.length === 0
      ? 'Keep at least one patient, or file the fax as one document.'
      : rows.some((row) => row.pages.length === 0)
        ? 'Give each patient at least one page, or remove them.'
        : rows.length === 1 && rows[0].pages.length === faxPatients.pageCount
          ? 'One patient with every page is the whole fax — use "File as one document".'
          : rows.some((row) => !row.patient || !row.title.trim())
            ? 'Pick a chart and enter a title for each patient.'
            : isLab && !labName.trim()
              ? 'Enter the lab name.'
              : needsReview && !reviewConfirmed
                ? 'Confirm the flagged pages above.'
                : null
  const canSubmit = !missing

  const updateRow = (index: number, change: Partial<Row>) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...change } : row)))

  // Moves a page to row `target`, or out of every row when target is 'none'.
  const movePage = (page: number, target: string) =>
    setRows((prev) =>
      prev.map((row, i) => {
        const pages = row.pages.filter((p) => p !== page)
        if (String(i) === target) pages.push(page)
        return { ...row, pages: pages.sort((a, b) => a - b) }
      }),
    )

  const submit = async () => {
    if (claimedPlan) {
      // Re-sending the claimed plan unchanged is what finishes it on the server.
      faxSplit.mutate(claimedPlan)
      return
    }
    if (needsReview) {
      try {
        await patientReview.mutateAsync(checkGroups)
      } catch {
        // useFaxPatientReview already showed staff why; nothing is filed.
        return
      }
    }
    faxSplit.mutate({
      documentType,
      labName: isLab ? labName.trim() : undefined,
      referringProviderId: isLab ? idOf(referringProvider) : undefined,
      notifyPatient: isLab && notifyPatient,
      groups: rows.map((row) => ({
        pages: row.pages,
        patientId: idOf(row.patient) ?? '',
        title: row.title.trim(),
      })),
    })
  }

  const pageNumbers = Array.from({ length: faxPatients.pageCount }, (_, i) => i + 1)
  const pageOptions = [
    ...rows.map((row, i) => ({
      value: String(i),
      label: `Patient ${i + 1}${row.name ? ` — ${row.name}` : ''}`,
    })),
    { value: 'none', label: 'Leave out' },
  ]
  const rowValueOf = (page: number) => {
    const index = rows.findIndex((row) => row.pages.includes(page))
    return index === -1 ? 'none' : String(index)
  }

  return (
    <div className="flex flex-col gap-3">
      {readOnly ? (
        <Alert color="yellow" p="sm">
          <Text size="sm">Filing was interrupted. Finish filing to complete it.</Text>
        </Alert>
      ) : (
        <Alert color="yellow" p="sm">
          <Text size="sm" fw={600}>
            {groups.length >= 2
              ? `${groups.length} patients found in this fax.`
              : 'Split this fax by patient.'}
          </Text>
          <Text size="sm" mt={4}>
            Each patient&apos;s pages will be saved as a separate document to that patient&apos;s
            chart. The original fax stays in the inbox.
          </Text>
          <Button
            variant="subtle"
            size="compact-sm"
            mt="xs"
            ml={-8}
            onClick={onFileAsOne}
            disabled={locked}
          >
            File as one document
          </Button>
        </Alert>
      )}

      <Select
        label="Document type"
        data={[
          { label: 'Labs/Imaging', value: 'lab' },
          { label: 'Document', value: 'document' },
        ]}
        value={documentType}
        allowDeselect={false}
        onChange={(value) => value && setDocumentType(value as FaxDocumentType)}
        disabled={locked}
      />
      {isLab && (
        <TextInput
          label="Lab"
          placeholder="Lab name"
          value={labName}
          onChange={(e) => setLabName(e.currentTarget.value)}
          disabled={locked}
        />
      )}

      {rows.map((row, index) => (
        <section
          key={row.key}
          aria-label={`Patient ${index + 1}`}
          className="flex flex-col gap-2 rounded border border-gray-200 p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Text size="sm" fw={600}>
                {formatPages(row.pages)}
              </Text>
              <PageLinks pages={row.pages} onViewPage={onViewPage} />
            </div>
            {!readOnly && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={`Remove patient ${index + 1}`}
                disabled={locked}
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
              >
                <IconX size={14} />
              </ActionIcon>
            )}
          </div>
          {row.name && (
            <Text size="xs" c="dimmed">
              On the fax: {row.name}
              {row.dob && ` · DOB ${row.dob}`}
            </Text>
          )}
          {/* Once the row has a chart, the patient check lists these pages itself (PD-2055). */}
          {!checkOf(index) && (
            <GuessedPagesNote pages={row.pages.filter((page) => guessed.has(page))} />
          )}
          <ResourceInput
            // defaultValue is uncontrolled: remount so a changed patient shows.
            key={row.patient?.reference ?? 'none'}
            label="Chart"
            resourceType="Patient"
            name={`chart-${index}`}
            disabled={locked}
            defaultValue={row.patient ?? undefined}
            onChange={(patient) =>
              updateRow(index, { patient: patient ? createReference(patient) : null })
            }
          />
          <FaxPatientWarnings group={checkOf(index)} />
          <TextInput
            label="Title"
            value={row.title}
            onChange={(e) => updateRow(index, { title: e.currentTarget.value })}
            disabled={locked}
          />
        </section>
      ))}

      {!readOnly && (
        <Button
          variant="subtle"
          size="compact-sm"
          ml={-8}
          mr="auto"
          leftSection={<IconPlus size={14} />}
          onClick={addRow}
          disabled={locked}
        >
          Add patient
        </Button>
      )}

      {!readOnly && pageNumbers.length > 0 && (
        <div>
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            ml={-8}
            leftSection={pagesOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            aria-expanded={pagesOpen}
            onClick={() => setPagesOpen((open) => !open)}
          >
            Move pages
          </Button>
          {pagesOpen && (
            <div className="mt-2 flex flex-col gap-1">
              {pageNumbers.map((page) => (
                <div key={page} className="flex items-center gap-2">
                  <Text size="xs" c="dimmed" className="w-12 shrink-0">
                    Page {page}
                  </Text>
                  <Select
                    className="flex-1"
                    size="xs"
                    aria-label={`Page ${page}`}
                    data={pageOptions}
                    value={rowValueOf(page)}
                    allowDeselect={false}
                    onChange={(value) => value && movePage(page, value)}
                    disabled={locked}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isLab && (
        <>
          <ResourceInput
            key={referringProvider?.reference ?? 'none'}
            label="Referring provider"
            resourceType="Practitioner"
            name="referringProvider"
            disabled={locked}
            defaultValue={referringProvider ?? undefined}
            searchCriteria={{
              '_has:PractitionerRole:practitioner:role': SNOMED_USER_ROLES.MEDICAL_DOCTOR.code,
            }}
            onChange={(practitioner) =>
              setReferringProvider(practitioner ? createReference(practitioner) : null)
            }
          />
          <Checkbox
            label="Email the patient an invitation to schedule a follow-up"
            checked={notifyPatient}
            onChange={(e) => setNotifyPatient(e.currentTarget.checked)}
            disabled={locked}
          />
        </>
      )}

      {!readOnly && needsReview && (
        <FaxPatientReviewConfirm
          checked={reviewConfirmed}
          onChange={setReviewConfirmed}
          disabled={locked}
        />
      )}
      <Button
        loading={faxSplit.isPending || patientReview.isPending}
        disabled={!readOnly && !canSubmit}
        onClick={submit}
      >
        {readOnly
          ? 'Finish filing'
          : `Split & file to ${rows.length} ${rows.length === 1 ? 'chart' : 'charts'}`}
      </Button>
      {!readOnly && missing && (
        <Text size="xs" c="dimmed">
          {missing}
        </Text>
      )}
    </div>
  )
}

function GuessedPagesNote({ pages }: { pages: number[] }) {
  if (pages.length === 0) return null
  const one = pages.length === 1
  return (
    <Text size="xs" c="orange.8">
      {formatPages(pages)} {one ? 'does not' : 'do not'} name a patient — check{' '}
      {one ? 'it belongs' : 'they belong'} here.
    </Text>
  )
}

function PageLinks({ pages, onViewPage }: { pages: number[]; onViewPage: (page: number) => void }) {
  if (pages.length === 0) return null
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5">
      <Text size="xs" c="dimmed">
        View
      </Text>
      {pages.map((page) => (
        <Anchor
          key={page}
          component="button"
          type="button"
          size="xs"
          aria-label={`View page ${page}`}
          onClick={() => onViewPage(page)}
        >
          {page}
        </Anchor>
      ))}
    </div>
  )
}

function FiledDocuments({ filed }: { filed: NonNullable<FaxPatients['split']>['filed'] }) {
  return (
    <div className="flex flex-col gap-3">
      <Alert color="green" p="sm">
        <Text size="sm" fw={600}>
          {filed.length === 0
            ? 'Split, but every document was taken off its chart.'
            : `Split and filed to ${filed.length} ${filed.length === 1 ? 'chart' : 'charts'}.`}
        </Text>
        <Text size="sm" mt={4}>
          The original fax stays in the inbox.
        </Text>
      </Alert>
      {filed.map((doc) => (
        <div
          key={doc.documentReferenceId}
          className="flex flex-col gap-1 rounded border border-gray-200 p-3"
        >
          <Anchor component={Link} to={`/patients/${doc.patientId}`} size="sm" fw={600}>
            {doc.patientDisplay}
          </Anchor>
          <Text size="sm">{doc.title}</Text>
          <Text size="xs" c="dimmed">
            {formatPages(doc.pages)}
          </Text>
        </div>
      ))}
    </div>
  )
}

function rowsFromGroups(groups: FaxPatientGroup[]): Row[] {
  return groups.map((group, index) => ({
    key: index,
    pages: group.pages,
    patient: group.suggestedPatient
      ? {
          reference: `Patient/${group.suggestedPatient.id}`,
          display: group.suggestedPatient.display,
        }
      : null,
    title: '',
    name: group.name,
    dob: group.dob,
  }))
}

// A split opened by hand (OCR found fewer than two patients) starts with two rows to move pages into.
function withAtLeastTwoRows(rows: Row[]): Row[] {
  const padded = [...rows]
  while (padded.length < 2) {
    padded.push({ key: padded.length, pages: [], patient: null, title: '', name: null, dob: null })
  }
  return padded
}

// A claimed split carries no suggestions; the chart picker resolves the bare reference itself.
function rowsFromPlan(plan: FaxSplitPlan, groups: FaxPatientGroup[]): Row[] {
  return plan.groups.map((planGroup, index) => {
    const onFax = groups.find((g) => g.pages.includes(planGroup.pages[0]))
    return {
      key: index,
      pages: planGroup.pages,
      patient: { reference: `Patient/${planGroup.patientId}` },
      title: planGroup.title,
      name: onFax?.name ?? null,
      dob: onFax?.dob ?? null,
    }
  })
}

const idOf = (reference: Reference | null) => reference?.reference?.split('/').pop()
